import { useCallback, useEffect, useMemo, useState } from "react";
import { formatKatanaSymbol, useKatanaWsConnection } from "@/hooks/exchanges/direct-ws/katana";
import type { KatanaSubscription } from "@/hooks/exchanges/direct-ws/katana";
import type { KatanaTickerMessage } from "@/hooks/exchanges/direct-ws/katana/types";
import type { RealtimeTickerData } from "@/hooks/exchanges/types";
import { safeDecimal } from "@/utils/hyperliquid/numeric";

const TICKER_CHANNEL = "tickers" as const;

interface UseKatanaTickerWSResult {
  ticker: RealtimeTickerData | undefined;
  isConnected: boolean;
  error: string | null;
}

/**
 * Hook for subscribing to Katana ticker data via the shared WebSocket.
 *
 * Uses the declarative `id` + `subscription` API on `useKatanaWsConnection`,
 * so the wire `subscribe`/`unsubscribe` envelopes are ref-counted across
 * components. Maps Katana's deflated wire keys onto the exchange-agnostic
 * `RealtimeTickerData` shape; the 24h price change is derived from
 * `(close - open) / open * 100` since Katana doesn't emit a pre-computed
 * percentage on the channel.
 */
export const useKatanaTickerWS = ({
  pairSymbol,
  enabled = true,
}: {
  pairSymbol: string | undefined;
  enabled?: boolean;
}): UseKatanaTickerWSResult => {
  const [ticker, setTicker] = useState<RealtimeTickerData | undefined>(undefined);
  const [tickerError, setTickerError] = useState<string | null>(null);

  const market = pairSymbol ? formatKatanaSymbol(pairSymbol) : "";

  useEffect(() => {
    setTicker(undefined);
    setTickerError(null);
  }, [market]);

  const onMessage = useCallback(
    (msg: unknown) => {
      if (!market) return;

      const typed = msg as KatanaTickerMessage;
      if (typed.type !== TICKER_CHANNEL || !typed.data || typed.data.m !== market) return;

      try {
        const d = typed.data;
        const openDec = safeDecimal(d.o);
        const closeDec = safeDecimal(d.c);
        const priceChange24h = openDec.isZero() ? 0 : closeDec.minus(openDec).div(openDec).mul(100).toNumber();

        // Katana streams open interest in BASE asset units (e.g. BTC). The
        // platform — and every other exchange that flows through
        // `RealtimeTickerData` — exposes it in quote / USD terms. Convert via
        // the mark price (the fair-value reference exchanges use for OI
        // valuation); fall back to the last price if a frame happens to omit
        // it.
        const openInterestBase = safeDecimal(d.oi);
        const markDec = d.mp !== undefined ? safeDecimal(d.mp) : closeDec;
        const openInterestUsd = openInterestBase.mul(markDec).toString();

        setTicker({
          volume24h: d.v,
          // Katana settles in VBUSDC, so quote volume is effectively the USD figure.
          volumeUsd24h: d.q,
          priceChange24h,
          openInterest: openInterestUsd,
          lastPrice: d.c,
          high24h: d.h,
          low24h: d.l,
          bestBid: d.b,
          bestAsk: d.a,
        });
        setTickerError(null);
      } catch {
        setTickerError("Failed to parse ticker update");
      }
    },
    [market],
  );

  const subscription = useMemo<KatanaSubscription | undefined>(
    () => (market ? { name: TICKER_CHANNEL, markets: [market] } : undefined),
    [market],
  );

  const { isConnected, error: connectionError } = useKatanaWsConnection({
    id: market ? `${TICKER_CHANNEL}-${market}` : undefined,
    subscription,
    onMessage,
    enabled: enabled && !!pairSymbol,
  });

  return {
    ticker,
    isConnected,
    error: tickerError ?? connectionError,
  };
};
